import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faShieldAlt, faClipboardCheck, faBug,
  faSync, faFileExport, faSpinner, faExchangeAlt, faGlobe
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';
import WstgHeatmap from './WstgHeatmap';
import crossFrameworkService from '../../services/crossFrameworkService';
import { useLanguage } from '../../contexts/LanguageContext';

const REPORT_TYPES = [
  { id: 'wstg', label: 'WSTG Coverage', icon: faClipboardCheck, color: 'text-teal-400' },
  { id: 'waf', label: 'WAF Effectiveness', icon: faShieldAlt, color: 'text-blue-400' },
  { id: 'vuln', label: 'Vulnerabilities', icon: faBug, color: 'text-red-400' },
  { id: 'crossframework', label: 'Cross-Framework', icon: faGlobe, color: 'text-orange-400' },
  { id: 'compare', label: 'Before/After', icon: faExchangeAlt, color: 'text-purple-400' }
];

const WebTestReports = ({ webTarget, isDarkMode }) => {
  const { t } = useLanguage();
  const [selectedReport, setSelectedReport] = useState('wstg');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [compareIds, setCompareIds] = useState({ id1: '', id2: '' });

  useEffect(() => {
    if (webTarget?._id) {
      loadReport(selectedReport);
      loadScenarios();
    }
  }, [webTarget, selectedReport]);

  const loadScenarios = async () => {
    try {
      const res = await axios.get(`/authz-test/scenarios/${webTarget._id}`);
      setScenarios((res.data || []).filter(s => s.status === 'completed'));
    } catch {}
  };

  const loadReport = async (type) => {
    setLoading(true);
    try {
      if (type === 'crossframework') {
        // Load campaigns linked to this web target, then get coverage for each
        const campaignsRes = await axios.get(`/campaigns/by-web-target/${webTarget._id}`);
        const campaigns = campaignsRes.data || [];
        const coverageData = [];
        for (const c of campaigns.slice(0, 10)) {
          try {
            const coverage = await crossFrameworkService.getCoverage(c._id);
            coverageData.push({ campaign: c, coverage });
          } catch {}
        }
        setData({ campaigns, coverageData });
        setLoading(false);
        return;
      }
      let endpoint;
      switch (type) {
        case 'wstg': endpoint = `/web-reports/wstg/${webTarget._id}`; break;
        case 'waf': endpoint = `/web-reports/waf/${webTarget._id}`; break;
        case 'vuln': endpoint = `/web-reports/vulnerabilities/${webTarget._id}`; break;
        default: setData(null); setLoading(false); return;
      }
      const res = await axios.get(endpoint);
      setData(res.data);
    } catch (err) {
      console.error(`Failed to load ${type} report:`, err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!compareIds.id1 || !compareIds.id2) return;
    setLoading(true);
    try {
      const res = await axios.post('/web-reports/compare', { scenarioId1: compareIds.id1, scenarioId2: compareIds.id2 });
      setData(res.data);
    } catch (err) {
      console.error('Compare failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const text = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const selectClass = `text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;

  const renderReport = () => {
    if (loading) return <div className={`flex items-center justify-center h-64 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}><FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> {t('webTestReports.loadingReport')}</div>;
    if (!data) return <div className={`flex items-center justify-center h-64 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('webTestReports.noDataAvailable')}</div>;

    switch (selectedReport) {
      case 'wstg': return renderWstgReport();
      case 'waf': return renderWafReport();
      case 'vuln': return renderVulnReport();
      case 'crossframework': return renderCrossFrameworkReport();
      case 'compare': return renderCompareReport();
      default: return null;
    }
  };

  const renderWstgReport = () => (
    <div className="space-y-4 p-4">
      <WstgHeatmap webTarget={webTarget} isDarkMode={isDarkMode} />
      {data?.categories && (
        <div>
          <h3 className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{t('webTestReports.categoryBreakdown')}</h3>
          <table className="w-full text-xs">
            <thead><tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              <th className="text-left px-2 py-1">Category</th><th className="px-2 py-1">Total</th><th className="px-2 py-1">Tested</th><th className="px-2 py-1">Pass</th><th className="px-2 py-1">Fail</th><th className="px-2 py-1">Coverage</th>
            </tr></thead>
            <tbody>
              {data.categories.map(cat => (
                <tr key={cat.code} className={`border-t ${border}`}>
                  <td className={`px-2 py-1.5 ${text}`}>{cat.code} - {cat.name}</td>
                  <td className="px-2 py-1.5 text-center">{cat.total}</td>
                  <td className="px-2 py-1.5 text-center">{cat.tested}</td>
                  <td className="px-2 py-1.5 text-center text-green-400">{cat.pass}</td>
                  <td className="px-2 py-1.5 text-center text-red-400">{cat.fail}</td>
                  <td className="px-2 py-1.5 text-center">{cat.total > 0 ? Math.round((cat.tested / cat.total) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderWafReport = () => (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-4 gap-3">
        <div className={`p-3 rounded border ${border}`}>
          <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{data.overallBlockRate}%</div>
          <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('webTestReports.overallBlockRate')}</div>
        </div>
        <div className={`p-3 rounded border ${border}`}>
          <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{data.totalAttacks}</div>
          <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('webTestReports.totalAttacks')}</div>
        </div>
        <div className={`p-3 rounded border ${border}`}>
          <div className="text-2xl font-bold text-green-400">{data.totalBlocked}</div>
          <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('webTestReports.blocked')}</div>
        </div>
        <div className={`p-3 rounded border ${border}`}>
          <div className="text-2xl font-bold text-red-400">{data.totalPassed}</div>
          <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('webTestReports.passedThrough')}</div>
        </div>
      </div>

      {data.categoryResults?.length > 0 && (
        <div>
          <h3 className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{t('webTestReports.blockRateByCategory')}</h3>
          <div className="space-y-1.5">
            {data.categoryResults.map(cat => (
              <div key={cat.attackType} className="flex items-center gap-2">
                <span className={`text-xs w-28 truncate ${text}`}>{cat.attackType}</span>
                <div className="flex-1 h-4 bg-gray-700 rounded-full overflow-hidden flex">
                  <div className="h-full bg-green-600" style={{ width: `${cat.blockRate}%` }} />
                  <div className="h-full bg-red-600" style={{ width: `${100 - cat.blockRate}%` }} />
                </div>
                <span className={`text-xs w-12 text-right ${cat.blockRate < 50 ? 'text-red-400' : cat.blockRate < 80 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {cat.blockRate}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.recommendations?.length > 0 && (
        <div>
          <h3 className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{t('webTestReports.recommendations')}</h3>
          <div className="space-y-1">
            {data.recommendations.map((r, i) => (
              <div key={i} className={`text-xs px-3 py-2 rounded border ${border} ${r.priority === 'high' ? 'border-l-2 border-l-red-500' : r.priority === 'medium' ? 'border-l-2 border-l-yellow-500' : ''}`}>
                {r.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderVulnReport = () => (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-6 gap-3">
        <div className={`p-3 rounded border ${border} text-center`}>
          <div className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{data.totalFindings}</div>
          <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total</div>
        </div>
        {['critical', 'high', 'medium', 'low', 'informational'].map(sev => {
          const colors = { critical: 'text-red-500', high: 'text-orange-400', medium: 'text-yellow-400', low: 'text-green-400', informational: 'text-blue-400' };
          return (
            <div key={sev} className={`p-3 rounded border ${border} text-center`}>
              <div className={`text-xl font-bold ${colors[sev]}`}>{data.bySeverity?.[sev] || 0}</div>
              <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{sev}</div>
            </div>
          );
        })}
      </div>

      {data.riskScore && (
        <div className={`p-3 rounded border ${border} flex items-center gap-4`}>
          <div className={`text-3xl font-bold ${
            data.riskScore.grade === 'A' ? 'text-green-400' :
            data.riskScore.grade === 'B' ? 'text-blue-400' :
            data.riskScore.grade === 'C' ? 'text-yellow-400' :
            data.riskScore.grade === 'D' ? 'text-orange-400' : 'text-red-400'
          }`}>
            {data.riskScore.grade}
          </div>
          <div>
            <div className={`text-xs font-medium ${text}`}>{data.riskScore.label}</div>
            <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Risk Score: {data.riskScore.score}/{data.riskScore.maxPossible} ({data.riskScore.percent}%) - {data.riskScore.activeFindings} active findings
            </div>
          </div>
        </div>
      )}

      {data.findings?.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
            <th className="text-left px-2 py-1">Severity</th><th className="text-left px-2 py-1">Title</th><th className="text-left px-2 py-1">WSTG</th><th className="text-left px-2 py-1">Endpoint</th><th className="text-left px-2 py-1">Status</th>
          </tr></thead>
          <tbody>
            {data.findings.slice(0, 50).map(f => {
              const sevColors = { critical: 'bg-red-900/30 text-red-400', high: 'bg-orange-900/30 text-orange-400', medium: 'bg-yellow-900/30 text-yellow-400', low: 'bg-green-900/30 text-green-400', informational: 'bg-blue-900/30 text-blue-400' };
              return (
                <tr key={f._id} className={`border-t ${border}`}>
                  <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] ${sevColors[f.severity] || ''}`}>{f.severity}</span></td>
                  <td className={`px-2 py-1.5 ${text}`}>{f.title}</td>
                  <td className={`px-2 py-1.5 font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{f.wstgTest?.testId || '-'}</td>
                  <td className={`px-2 py-1.5 font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{f.endpoint?.method} {f.endpoint?.url || f.endpoint?.path || '-'}</td>
                  <td className="px-2 py-1.5"><span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{f.status}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderCrossFrameworkReport = () => {
    if (!data?.campaigns) return <div className={`p-8 text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No campaign data available</div>;

    return (
      <div className="space-y-4 p-4">
        <h3 className={`text-xs font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          <FontAwesomeIcon icon={faGlobe} className="mr-1 text-orange-400" />
          {t('webTestReports.crossFrameworkCoverage')}
        </h3>
        {data.campaigns.length === 0 ? (
          <div className={`p-6 text-center rounded border ${border}`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No campaigns linked to this web target. Create a WSTG campaign from Blue Team &gt; Campaigns with this web target selected.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.coverageData.map(({ campaign, coverage }) => (
              <div key={campaign._id} className={`p-3 rounded border ${border}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      campaign.framework === 'owasp_wstg' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    }`}>{campaign.framework === 'owasp_wstg' ? 'WSTG' : 'MITRE'}</span>
                    <span className={`text-sm font-medium ${text}`}>{campaign.name}</span>
                  </div>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{campaign.status}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-lg font-bold text-orange-500">{coverage.wstgTestsLinked}</div>
                    <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>WSTG Tests</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-red-500">{coverage.mitreTechniquesLinked}</div>
                    <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>MITRE Techniques</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-500">{coverage.totalLinks}</div>
                    <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Cross-Links</div>
                  </div>
                </div>
                <div className="mt-2 flex gap-4">
                  <div className="flex-1">
                    <div className={`text-[10px] mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>MITRE Tactic Coverage</div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${coverage.mitreTacticCoverage}%` }} />
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{coverage.mitreTacticCoverage}%</div>
                  </div>
                  <div className="flex-1">
                    <div className={`text-[10px] mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>WSTG Category Coverage</div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500 rounded-full" style={{ width: `${coverage.wstgCategoryCoverage}%` }} />
                    </div>
                    <div className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{coverage.wstgCategoryCoverage}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCompareReport = () => (
    <div className="p-4 space-y-3">
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('webTestReports.run1Before')}</label>
          <select value={compareIds.id1} onChange={e => setCompareIds(p => ({ ...p, id1: e.target.value }))} className={selectClass + ' w-full'}>
            <option value="">Select scenario...</option>
            {scenarios.map(s => <option key={s._id} value={s._id}>{s.name} ({new Date(s.createdAt).toLocaleDateString()})</option>)}
          </select>
        </div>
        <div className="flex-1">
          <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('webTestReports.run2After')}</label>
          <select value={compareIds.id2} onChange={e => setCompareIds(p => ({ ...p, id2: e.target.value }))} className={selectClass + ' w-full'}>
            <option value="">Select scenario...</option>
            {scenarios.map(s => <option key={s._id} value={s._id}>{s.name} ({new Date(s.createdAt).toLocaleDateString()})</option>)}
          </select>
        </div>
        <button onClick={handleCompare} disabled={!compareIds.id1 || !compareIds.id2 || loading}
          className="px-4 py-1.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50">
          {t('webTestReports.compare')}
        </button>
      </div>

      {data?.comparison && (
        <>
          <div className="flex gap-4">
            <span className="text-xs text-green-400">{data.summary.improved} improved</span>
            <span className="text-xs text-red-400">{data.summary.regressed} regressed</span>
            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{data.summary.unchanged} unchanged</span>
          </div>
          <table className="w-full text-xs">
            <thead><tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              <th className="text-left px-2 py-1">Method</th><th className="text-left px-2 py-1">URL</th><th className="text-left px-2 py-1">Before</th><th className="text-left px-2 py-1">After</th><th className="text-left px-2 py-1">Change</th>
            </tr></thead>
            <tbody>
              {data.comparison.map((c, i) => (
                <tr key={i} className={`border-t ${border} ${c.regressed ? 'bg-red-900/10' : c.improved ? 'bg-green-900/10' : ''}`}>
                  <td className={`px-2 py-1.5 font-mono ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{c.method}</td>
                  <td className={`px-2 py-1.5 font-mono truncate max-w-md ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{c.url}</td>
                  <td className={`px-2 py-1.5 ${c.before === 'bypassed' ? 'text-red-400' : c.before === 'enforced' ? 'text-green-400' : 'text-yellow-400'}`}>{c.before}</td>
                  <td className={`px-2 py-1.5 ${c.after === 'bypassed' ? 'text-red-400' : c.after === 'enforced' ? 'text-green-400' : 'text-yellow-400'}`}>{c.after}</td>
                  <td className={`px-2 py-1.5 ${c.regressed ? 'text-red-400 font-bold' : c.improved ? 'text-green-400' : 'text-gray-500'}`}>
                    {c.regressed ? 'REGRESSED' : c.improved ? 'Improved' : c.changed ? 'Changed' : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Report type selector */}
      <div className={`flex items-center border-b ${border} ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        {REPORT_TYPES.map(rt => (
          <button key={rt.id} onClick={() => { setSelectedReport(rt.id); if (rt.id !== 'compare') setData(null); }}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 ${
              selectedReport === rt.id
                ? `border-teal-400 ${rt.color}`
                : `border-transparent ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
            }`}>
            <FontAwesomeIcon icon={rt.icon} /> {rt.label}
          </button>
        ))}
      </div>

      {/* Report content */}
      <div className="flex-1 overflow-auto">
        {renderReport()}
      </div>
    </div>
  );
};

export default WebTestReports;
